import React from 'react';
import { Box } from '@mui/material';
import type { BodyPartType } from '../../utils/visualUtils';

interface CameraOverlayProps {
    type: BodyPartType;
}

/**
 * SVG overlay guides for different body part captures
 */
export default function CameraOverlay({ type }: CameraOverlayProps) {
    const renderOverlay = () => {
        switch (type) {
            case 'eyes':
                return (
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 600 450"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ position: 'absolute', top: 0, left: 0 }}
                    >
                        {/* Darken background except face area */}
                        <defs>
                            <mask id="faceMaskEyes">
                                <rect width="100%" height="100%" fill="white" />
                                <ellipse cx="300" cy="225" rx="140" ry="180" fill="black" />
                            </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#faceMaskEyes)" />

                        {/* Face oval guide with dashed border */}
                        <ellipse
                            cx="300"
                            cy="225"
                            rx="140"
                            ry="180"
                            fill="none"
                            stroke="#4169E1"
                            strokeWidth="3"
                            strokeDasharray="10,5"
                        />

                        {/* Eye circles */}
                        <circle cx="260" cy="205" r="25" fill="none" stroke="#4169E1" strokeWidth="2" />
                        <circle cx="340" cy="205" r="25" fill="none" stroke="#4169E1" strokeWidth="2" />

                        {/* Eye alignment text */}
                        <text
                            x="300"
                            y="380"
                            textAnchor="middle"
                            fill="white"
                            fontSize="16"
                            fontWeight="600"
                        >
                            Align eyes within circles
                        </text>
                    </svg>
                );

            case 'lips':
                return (
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 600 450"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ position: 'absolute', top: 0, left: 0 }}
                    >
                        {/* Darken background except face area */}
                        <defs>
                            <mask id="faceMaskLips">
                                <rect width="100%" height="100%" fill="white" />
                                <ellipse cx="300" cy="225" rx="140" ry="180" fill="black" />
                            </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#faceMaskLips)" />

                        {/* Face oval guide */}
                        <ellipse
                            cx="300"
                            cy="225"
                            rx="140"
                            ry="180"
                            fill="none"
                            stroke="#4169E1"
                            strokeWidth="3"
                            strokeDasharray="10,5"
                        />

                        {/* Lip outline */}
                        <ellipse
                            cx="300"
                            cy="260"
                            rx="45"
                            ry="20"
                            fill="none"
                            stroke="#4169E1"
                            strokeWidth="2"
                        />

                        {/* Center line for lips */}
                        <line
                            x1="255"
                            y1="260"
                            x2="345"
                            y2="260"
                            stroke="#4169E1"
                            strokeWidth="1.5"
                            strokeDasharray="3,2"
                        />

                        {/* Lip alignment text */}
                        <text
                            x="300"
                            y="380"
                            textAnchor="middle"
                            fill="white"
                            fontSize="16"
                            fontWeight="600"
                        >
                            Align lips within guide
                        </text>
                    </svg>
                );

            case 'tongue':
                return (
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 600 450"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ position: 'absolute', top: 0, left: 0 }}
                    >
                        {/* Darken background except face area */}
                        <defs>
                            <mask id="faceMaskTongue">
                                <rect width="100%" height="100%" fill="white" />
                                <ellipse cx="300" cy="225" rx="140" ry="180" fill="black" />
                            </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#faceMaskTongue)" />

                        {/* Face oval guide */}
                        <ellipse
                            cx="300"
                            cy="225"
                            rx="140"
                            ry="180"
                            fill="none"
                            stroke="#4169E1"
                            strokeWidth="3"
                            strokeDasharray="10,5"
                        />

                        {/* Tongue guide - rounded rectangle shape */}
                        <rect
                            x="275"
                            y="240"
                            width="50"
                            height="60"
                            rx="15"
                            ry="15"
                            fill="none"
                            stroke="#00CED1"
                            strokeWidth="2.5"
                        />

                        {/* Horizontal guide line */}
                        <line
                            x1="220"
                            y1="270"
                            x2="380"
                            y2="270"
                            stroke="#00CED1"
                            strokeWidth="1.5"
                            strokeDasharray="5,3"
                        />

                        {/* Tongue alignment text */}
                        <text
                            x="300"
                            y="380"
                            textAnchor="middle"
                            fill="white"
                            fontSize="16"
                            fontWeight="600"
                        >
                            Stick out tongue within guide
                        </text>
                    </svg>
                );

            case 'nails':
                return (
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 600 450"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ position: 'absolute', top: 0, left: 0 }}
                    >
                        {/* Darken background except hand area */}
                        <defs>
                            <mask id="handMask">
                                <rect width="100%" height="100%" fill="white" />
                                <rect x="200" y="150" width="200" height="150" rx="10" fill="black" />
                            </mask>
                        </defs>
                        <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#handMask)" />

                        {/* Hand/nails rectangular guide */}
                        <rect
                            x="200"
                            y="150"
                            width="200"
                            height="150"
                            rx="10"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeDasharray="10,5"
                        />

                        {/* Finger position guides */}
                        <line x1="240" y1="150" x2="240" y2="300" stroke="white" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                        <line x1="280" y1="150" x2="280" y2="300" stroke="white" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                        <line x1="320" y1="150" x2="320" y2="300" stroke="white" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
                        <line x1="360" y1="150" x2="360" y2="300" stroke="white" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />

                        {/* Nails alignment text */}
                        <text
                            x="300"
                            y="340"
                            textAnchor="middle"
                            fill="white"
                            fontSize="16"
                            fontWeight="600"
                        >
                            Place fingertips within rectangle
                        </text>
                    </svg>
                );

            default:
                return null;
        }
    };

    return <Box>{renderOverlay()}</Box>;
}